import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search, Filter, Trash2, ArrowLeft, Package, GitBranch,
  FileCode, Shield, CheckCircle, XCircle, Clock, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  getScans,
  deleteScan,
  formatScanType,
  formatDate
} from '@/services/litesabre';

const LiteSabreScans = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine base path from current location
  const basePath = location.pathname.startsWith('/litesabre') && !location.pathname.startsWith('/litesabre-pro') 
    ? '/litesabre' 
    : '/grc/litesabre';
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadScans();
  }, [typeFilter, statusFilter]);

  const loadScans = async () => {
    try {
      setLoading(true);
      const params = {};
      if (typeFilter) params.scan_type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      
      const data = await getScans(params);
      setScans(data);
    } catch (err) {
      console.error('Failed to load scans:', err);
      toast.error('Failed to load scan history');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (scanId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this scan?')) return;
    
    try {
      await deleteScan(scanId);
      toast.success('Scan deleted');
      loadScans();
    } catch (err) {
      toast.error('Failed to delete scan');
    }
  };

  const getScanIcon = (scanType) => {
    switch (scanType) {
      case 'dependency': return <Package className="w-5 h-5 text-blue-600" />;
      case 'container': return <FileCode className="w-5 h-5 text-purple-600" />;
      case 'iac': return <Shield className="w-5 h-5 text-green-600" />;
      case 'code': return <GitBranch className="w-5 h-5 text-orange-600" />;
      default: return <Package className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3" /> Completed
          </span>
        );
      case 'failed':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">
            <XCircle className="w-3 h-3" /> Failed
          </span>
        );
      case 'running':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">
            <RefreshCw className="w-3 h-3 animate-spin" /> Running
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
            <Clock className="w-3 h-3" /> {status}
          </span>
        );
    }
  };

  const filteredScans = scans.filter(scan =>
    scan.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    scan.target?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`${basePath}`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Scan History</h1>
            <p className="text-gray-500">View and manage past vulnerability scans</p>
          </div>
        </div>
        <Button onClick={() => navigate(`${basePath}/scan`)} className="bg-indigo-600 hover:bg-indigo-700">
          New Scan
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search scans..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <select
              className="border rounded-md px-3 py-2 text-sm"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All Types</option>
              <option value="dependency">Dependency</option>
              <option value="container">Container</option>
              <option value="iac">IaC</option>
              <option value="code">Code</option>
            </select>
            <select
              className="border rounded-md px-3 py-2 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="running">Running</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>
            <Button variant="outline" onClick={loadScans}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Scans List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="mt-2 text-gray-500">Loading scans...</p>
            </div>
          ) : filteredScans.length === 0 ? (
            <div className="p-8 text-center">
              <Search className="w-8 h-8 mx-auto text-gray-300" />
              <p className="mt-2 text-gray-500">No scans found</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate(`${basePath}/scan`)}
              >
                Start Your First Scan
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {filteredScans.map((scan) => (
                <div
                  key={scan.id}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition"
                  onClick={() => navigate(`${basePath}/scans/${scan.id}`)}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      {getScanIcon(scan.scan_type)}
                    </div>
                    <div>
                      <p className="font-medium">{scan.name}</p>
                      <p className="text-sm text-gray-500">
                        {scan.target} • {formatScanType(scan.scan_type)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    {/* Vulnerability Counts — always render something so
                        failed/clean scans don't look like blank rows */}
                    <div className="flex items-center gap-2 text-sm" data-testid={`scan-row-summary-${scan.id}`}>
                      {scan.status === 'failed' ? (
                        <span className="px-2 py-1 bg-red-50 text-red-700 rounded border border-red-200 text-xs" title={scan.error_message || 'Scan failed'}>
                          Scan failed
                        </span>
                      ) : scan.summary ? (
                        <>
                          {scan.summary.critical_count > 0 && (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                              {scan.summary.critical_count} Critical
                            </span>
                          )}
                          {scan.summary.high_count > 0 && (
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">
                              {scan.summary.high_count} High
                            </span>
                          )}
                          {scan.summary.medium_count > 0 && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                              {scan.summary.medium_count} Medium
                            </span>
                          )}
                          {(scan.summary.total_vulnerabilities || 0) === 0 && scan.status === 'completed' ? (
                            <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded border border-emerald-200 text-xs">
                              No findings
                            </span>
                          ) : (
                            <span className="text-gray-500">
                              {scan.summary.total_vulnerabilities || 0} total
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Awaiting results</span>
                      )}
                    </div>
                    
                    {/* Status */}
                    {getStatusBadge(scan.status)}
                    
                    {/* Time */}
                    <div className="text-right min-w-[120px]">
                      <p className="text-sm text-gray-500">{formatDate(scan.created_at)}</p>
                    </div>
                    
                    {/* Actions */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => handleDelete(scan.id, e)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LiteSabreScans;
